'use client';

import { c as _c } from "react/compiler-runtime";
import { useReducedMotion } from 'motion/react';
import { SPARTAN_PRODUCT_ENGINE_REMARK_REVEAL_SCROLL_OFFSET, SPARTAN_PRODUCT_ENGINE_REVEAL_SCROLL_OFFSET } from '@/app/lcv/components/sections/spartan/products/engine/constants';
import { SpartanProductEngineStageGradient } from '@/app/lcv/components/sections/spartan/products/engine/stage/gradient';
import { SpartanProductEngineStageMedia } from '@/app/lcv/components/sections/spartan/products/engine/stage/media';
import styles from '@/app/lcv/components/sections/spartan/products/engine/styles.module.css';
import { SPARTAN_PRODUCT_ENGINE_TRACKING } from '@/app/lcv/components/sections/spartan/products/engine/tracking';
import type { SpartanProductEngineStagePalette } from '@/app/lcv/components/sections/spartan/products/engine/types';
import { SpartanProductEngineCard } from '@/app/lcv/components/sections/spartan/products/engine/view/card';
import { SpartanProductEngineMediaPreloader } from '@/app/lcv/components/sections/spartan/products/engine/view/media-preloader';
import type { SpartanProductEngineViewProps } from '@/app/lcv/components/sections/spartan/products/engine/view/types';
import { useEngineMediaController } from '@/app/lcv/components/sections/spartan/products/engine/view/useEngineMediaController';
import { getSpartanRevealElementProps, getSpartanRevealGroupProps, SPARTAN_REVEAL_PRESET, SPARTAN_REVEAL_ROLE } from '@/app/lcv/components/sections/spartan/products/shared/reveal';
import { cn } from '@/app/utils/cn';
import { createDataAttributes } from '@/app/utils/createDataAttributes';
const STAGE_PALETTE_CLASS: Record<SpartanProductEngineStagePalette, string> = {
  black: styles.paletteBlack,
  blue: styles.paletteBlue,
  orange: styles.paletteOrange,
  lime: styles.paletteLime
};
export const SpartanProductEngineView = t0 => {
  const $ = _c(76);
  const {
    engines,
    cta,
    theme: t1
  } = t0;
  const theme = t1 === undefined ? "dark" : t1;
  const reducedMotion = useReducedMotion() ?? false;
  let t2;
  if ($[0] !== engines || $[1] !== reducedMotion) {
    t2 = {
      engines,
      reducedMotion
    };
    $[0] = engines;
    $[1] = reducedMotion;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const {
    rootRef,
    displayedIndex,
    requestedIndex,
    switchPhase,
    readyIndices,
    shouldLoadMedia,
    handleEngineReady,
    handleSelectIndex
  } = useEngineMediaController(t2);
  const selectedEngine = engines[displayedIndex] ?? engines[0];
  let t3;
  if ($[3] !== displayedIndex || $[4] !== readyIndices) {
    t3 = readyIndices.has(displayedIndex);
    $[3] = displayedIndex;
    $[4] = readyIndices;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const isDisplayedMediaReady = t3;
  if (!selectedEngine) {
    return null;
  }
  const {
    stageMedia
  } = selectedEngine;
  const hasMediaImage = !!stageMedia?.image.src;
  let t4;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = getSpartanRevealElementProps({
      preset: SPARTAN_REVEAL_PRESET.media,
      role: SPARTAN_REVEAL_ROLE.fade,
      scrollOffset: SPARTAN_PRODUCT_ENGINE_REVEAL_SCROLL_OFFSET,
      style: {
        "--spartan-reveal-duration": "500ms"
      }
    });
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const t5 = STAGE_PALETTE_CLASS[selectedEngine.stagePalette];
  let t6;
  if ($[7] !== t5) {
    t6 = cn(styles.root, t5);
    $[7] = t5;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  let t7;
  if ($[9] !== isDisplayedMediaReady) {
    t7 = isDisplayedMediaReady ? {
      "data-media-ready": ""
    } : {};
    $[9] = isDisplayedMediaReady;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  let t8;
  if ($[11] !== hasMediaImage) {
    t8 = hasMediaImage ? {
      "data-has-media-image": ""
    } : {};
    $[11] = hasMediaImage;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  let t9;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <div aria-hidden={true} className={styles.aspectSpacer} />;
    $[13] = t9;
  } else {
    t9 = $[13];
  }
  let t10;
  let t11;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = getSpartanRevealGroupProps(SPARTAN_REVEAL_PRESET.media, {
      scrollOffset: SPARTAN_PRODUCT_ENGINE_REVEAL_SCROLL_OFFSET
    });
    t11 = <SpartanProductEngineStageGradient />;
    $[14] = t10;
    $[15] = t11;
  } else {
    t10 = $[14];
    t11 = $[15];
  }
  let t12;
  if ($[16] !== displayedIndex || $[17] !== hasMediaImage || $[18] !== shouldLoadMedia || $[19] !== stageMedia || $[20] !== switchPhase) {
    t12 = !!hasMediaImage && !!stageMedia && <SpartanProductEngineStageMedia media={stageMedia} index={displayedIndex} loading={shouldLoadMedia ? "eager" : "lazy"} paused={switchPhase === "out"} preventLoadVideo={!shouldLoadMedia} />;
    $[16] = displayedIndex;
    $[17] = hasMediaImage;
    $[18] = shouldLoadMedia;
    $[19] = stageMedia;
    $[20] = switchPhase;
    $[21] = t12;
  } else {
    t12 = $[21];
  }
  let t13;
  if ($[22] !== t12) {
    t13 = <div className={styles.mediaMotion}>{t12}</div>;
    $[22] = t12;
    $[23] = t13;
  } else {
    t13 = $[23];
  }
  let t14;
  if ($[24] !== hasMediaImage) {
    t14 = hasMediaImage ? <div aria-hidden={true} className={styles.mediaScrim} /> : null;
    $[24] = hasMediaImage;
    $[25] = t14;
  } else {
    t14 = $[25];
  }
  let t15;
  if ($[26] !== t10 || $[27] !== t13 || $[28] !== t14) {
    t15 = <div className={styles.mediaStage} {...t10}>{t11}{t13}{t14}</div>;
    $[26] = t10;
    $[27] = t13;
    $[28] = t14;
    $[29] = t15;
  } else {
    t15 = $[29];
  }
  let t16;
  if ($[30] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = <div aria-hidden={true} className={styles.layoutSpacer} />;
    $[30] = t16;
  } else {
    t16 = $[30];
  }
  let t17;
  if ($[31] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = getSpartanRevealGroupProps(SPARTAN_REVEAL_PRESET.content, {
      scrollOffset: SPARTAN_PRODUCT_ENGINE_REVEAL_SCROLL_OFFSET
    });
    $[31] = t17;
  } else {
    t17 = $[31];
  }
  let t18;
  if ($[32] !== cta || $[33] !== displayedIndex || $[34] !== engines || $[35] !== handleSelectIndex || $[36] !== requestedIndex || $[37] !== selectedEngine || $[38] !== shouldLoadMedia || $[39] !== theme) {
    t18 = <SpartanProductEngineCard engines={engines} selectedIndex={displayedIndex} tabIndex={requestedIndex} selectedEngine={selectedEngine} cta={cta} theme={theme} shouldLoadMedia={shouldLoadMedia} onSelectIndex={handleSelectIndex} />;
    $[32] = cta;
    $[33] = displayedIndex;
    $[34] = engines;
    $[35] = handleSelectIndex;
    $[36] = requestedIndex;
    $[37] = selectedEngine;
    $[38] = shouldLoadMedia;
    $[39] = theme;
    $[40] = t18;
  } else {
    t18 = $[40];
  }
  let t19;
  if ($[41] !== t17 || $[42] !== t18) {
    t19 = <div className={styles.cardColumn} {...t17}>{t18}</div>;
    $[41] = t17;
    $[42] = t18;
    $[43] = t19;
  } else {
    t19 = $[43];
  }
  let t20;
  if ($[44] !== rootRef || $[45] !== switchPhase || $[46] !== t15 || $[47] !== t19 || $[48] !== t6 || $[49] !== t7 || $[50] !== t8) {
    t20 = <div ref={rootRef} className={t6} data-switch-phase={switchPhase} {...t7} {...t8}>{t9}{t15}{t16}{t19}</div>;
    $[44] = rootRef;
    $[45] = switchPhase;
    $[46] = t15;
    $[47] = t19;
    $[48] = t6;
    $[49] = t7;
    $[50] = t8;
    $[51] = t20;
  } else {
    t20 = $[51];
  }
  let t21;
  if ($[52] !== t20 || $[53] !== t4) {
    t21 = <div {...t4}>{t20}</div>;
    $[52] = t20;
    $[53] = t4;
    $[54] = t21;
  } else {
    t21 = $[54];
  }
  let t22;
  if ($[55] === Symbol.for("react.memo_cache_sentinel")) {
    t22 = cn(styles.remarkStack, "text-caption where-span:text-inline-wrap mt-6 grid w-full text-gray-600 md:mt-8 lg:mt-10");
    $[55] = t22;
  } else {
    t22 = $[55];
  }
  let t23;
  if ($[56] === Symbol.for("react.memo_cache_sentinel")) {
    t23 = getSpartanRevealElementProps({
      preset: SPARTAN_REVEAL_PRESET.content,
      role: SPARTAN_REVEAL_ROLE.fade,
      scrollOffset: SPARTAN_PRODUCT_ENGINE_REMARK_REVEAL_SCROLL_OFFSET,
      style: {
        "--spartan-reveal-duration": "500ms"
      }
    });
    $[56] = t23;
  } else {
    t23 = $[56];
  }
  let t24;
  if ($[57] !== displayedIndex || $[58] !== engines) {
    let t25;
    if ($[60] !== displayedIndex) {
      t25 = (engine, index) => {
        const isActive = index === displayedIndex;
        return <div key={engine.engineKey} className={cn("col-start-1 row-start-1", styles.remarkItem, {
          [styles.remarkItemActive]: isActive
        }, !isActive && "pointer-events-none")} aria-hidden={!isActive} dangerouslySetInnerHTML={{
          __html: engine.remark
        }} {...createDataAttributes({
          test: SPARTAN_PRODUCT_ENGINE_TRACKING.itemRemark(index)
        })} />;
      };
      $[60] = displayedIndex;
      $[61] = t25;
    } else {
      t25 = $[61];
    }
    t24 = engines.map(t25);
    $[57] = displayedIndex;
    $[58] = engines;
    $[59] = t24;
  } else {
    t24 = $[59];
  }
  let t25;
  if ($[62] !== switchPhase || $[63] !== t23 || $[64] !== t24) {
    t25 = <div className={t22} data-switch-phase={switchPhase} {...t23}>{t24}</div>;
    $[62] = switchPhase;
    $[63] = t23;
    $[64] = t24;
    $[65] = t25;
  } else {
    t25 = $[65];
  }
  let t26;
  if ($[66] !== engines || $[67] !== handleEngineReady || $[68] !== readyIndices || $[69] !== requestedIndex || $[70] !== shouldLoadMedia) {
    t26 = <SpartanProductEngineMediaPreloader engines={engines} enabled={shouldLoadMedia} priorityIndex={requestedIndex} readyIndices={readyIndices} onEngineReady={handleEngineReady} />;
    $[66] = engines;
    $[67] = handleEngineReady;
    $[68] = readyIndices;
    $[69] = requestedIndex;
    $[70] = shouldLoadMedia;
    $[71] = t26;
  } else {
    t26 = $[71];
  }
  let t27;
  if ($[72] !== t21 || $[73] !== t25 || $[74] !== t26) {
    t27 = <>{t21}{t25}{t26}</>;
    $[72] = t21;
    $[73] = t25;
    $[74] = t26;
    $[75] = t27;
  } else {
    t27 = $[75];
  }
  return t27;
};